import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Alert, Spinner, Button, Modal, Image } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import ReviewList from './ReviewList';

export default function UserProfileView({ currentUser }) {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [followingData, setFollowingData] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    if (currentUser && currentUser._id !== userId) {
      fetchFollowStatus();
    }
    // eslint-disable-next-line
  }, [userId, currentUser]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`/api/users/${userId}`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else if (response.status === 404) {
        setError('User not found');
      } else {
        setError('Failed to load user profile');
      }
    } catch (err) {
      setError('Network error loading profile');
    } finally {
      setLoading(false);
    }
    return null;
  };

  const fetchFollowStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`/api/users/${userId}/follow-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.following);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleFollowToggle = async () => {
    setFollowLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const endpoint = isFollowing ? 'unfollow' : 'follow';
      const response = await fetch(`/api/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Refetch and set the latest user profile
        const newUser = await fetchUserProfile();
        if (newUser) setUser(newUser);
        await fetchFollowStatus();
      }
    } catch (err) {
      // ignore
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getProfileImageUrl = () => {
    if (user?.profileImage) {
      // If it's already a full URL, return as is
      if (user.profileImage.startsWith('http')) {
        return user.profileImage;
      }
      // Otherwise, prepend the server URL
      return `http://localhost:5000${user.profileImage}`;
    }
    return null;
  };

  const fetchFollowersData = async () => {
    if (!user?.followers || user.followers.length === 0) {
      setFollowersData([]);
      return;
    }
    
    setFollowersLoading(true);
    try {
      const response = await fetch(`/api/users/list?ids=${user.followers.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        setFollowersData(data);
      } else {
        console.error('Failed to fetch followers data');
        setFollowersData([]);
      }
    } catch (err) {
      console.error('Error fetching followers data:', err);
      setFollowersData([]);
    } finally {
      setFollowersLoading(false);
    }
  };

  const handleFollowersClick = () => {
    setShowFollowersModal(true);
    fetchFollowersData();
  };

  const fetchFollowingData = async () => {
    if (!user?.following || user.following.length === 0) {
      setFollowingData([]);
      return;
    }
    
    setFollowingLoading(true);
    try {
      const response = await fetch(`/api/users/list?ids=${user.following.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        setFollowingData(data);
      } else {
        console.error('Failed to fetch following data');
        setFollowingData([]);
      }
    } catch (err) {
      console.error('Error fetching following data:', err);
      setFollowingData([]);
    } finally {
      setFollowingLoading(false);
    }
  };

  const handleFollowingClick = () => {
    setShowFollowingModal(true);
    fetchFollowingData();
  };

  if (loading) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading user profile...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="my-4">
        <Alert variant="warning">
          <h4>User Not Found</h4>
          <p>The requested user could not be found.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Row>
        <Col>
          {/* User Info Card */}
          <Card className="mb-4">
            <Card.Body>
              <div className="text-center">
                {getProfileImageUrl() ? (
                  <Image
                    src={getProfileImageUrl()}
                    alt="Profile"
                    roundedCircle
                    style={{ 
                      width: '100px', 
                      height: '100px', 
                      objectFit: 'cover',
                      margin: '0 auto 20px'
                    }}
                    className="border"
                  />
                ) : (
                  <div style={{ 
                    width: '100px', 
                    height: '100px', 
                    borderRadius: '50%', 
                    backgroundColor: '#007bff', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '2rem',
                    color: 'white'
                  }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <h2>{user?.username}</h2>
                {/* Follower/Following Counts */}
                <div className="d-flex justify-content-center mb-2" style={{ gap: 24 }}>
                  <div
                    className="text-center"
                    style={{ cursor: 'pointer', borderRadius: 8, padding: '4px 12px', border: '1px solid #eee', minWidth: 60 }}
                    onClick={handleFollowersClick}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{user.followersCount ?? user.followers?.length ?? 0}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Followers</div>
                  </div>
                  <div
                    className="text-center"
                    style={{ cursor: 'pointer', borderRadius: 8, padding: '4px 12px', border: '1px solid #eee', minWidth: 60 }}
                    onClick={handleFollowingClick}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{user.followingCount ?? user.following?.length ?? 0}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Following</div>
                  </div>
                </div>
                <Modal show={showFollowersModal} onHide={() => setShowFollowersModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Followers</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {followersLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" /> Loading...
                      </div>
                    ) : followersData.length > 0 ? (
                      <div>
                        <ul className="list-unstyled">
                          {followersData.map((follower) => (
                            <li key={follower._id} className="mb-2">
                              <Link 
                                to={currentUser && currentUser._id === follower._id ? "/profile" : `/user/${follower._id}`}
                                style={{ 
                                  textDecoration: 'none', 
                                  color: 'inherit',
                                  cursor: 'pointer'
                                }}
                                onClick={() => setShowFollowersModal(false)}
                              >
                                {follower.username}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p>No followers found.</p>
                    )}
                  </Modal.Body>
                </Modal>
                <Modal show={showFollowingModal} onHide={() => setShowFollowingModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Following</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {followingLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" /> Loading...
                      </div>
                    ) : followingData.length > 0 ? (
                      <div>
                        <ul className="list-unstyled">
                          {followingData.map((following) => (
                            <li key={following._id} className="mb-2">
                              <Link 
                                to={currentUser && currentUser._id === following._id ? "/profile" : `/user/${following._id}`}
                                style={{ 
                                  textDecoration: 'none', 
                                  color: 'inherit',
                                  cursor: 'pointer'
                                }}
                                onClick={() => setShowFollowingModal(false)}
                              >
                                {following.username}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p>Not following anyone.</p>
                    )}
                  </Modal.Body>
                </Modal>
                {/* Follow Button: only show if not viewing own profile and logged in */}
                {currentUser && currentUser._id !== userId && (
                  <Button
                    variant={isFollowing ? 'success' : 'outline-primary'}
                    size="sm"
                    style={{ minWidth: 100, marginBottom: 10 }}
                    disabled={followLoading}
                    onClick={handleFollowToggle}
                  >
                    {followLoading ? (isFollowing ? 'Unfollowing...' : 'Following...') : (isFollowing ? 'Following' : 'Follow')}
                  </Button>
                )}
                <p className="text-muted">
                  Member since {user?.createdAt ? formatDate(user.createdAt) : 'Unknown'}
                </p>
              </div>
            </Card.Body>
          </Card>

          {/* User's Reviews */}
          <ReviewList 
            mode="other-user"
            showItemInfo={true}
            userId={userId}
            currentUser={currentUser}
            profileUsername={user?.username}
          />
        </Col>
      </Row>
    </Container>
  );
} 